import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import {
  paymentTermsHtmlToNullable,
  toPaymentTermsEditorHtml,
} from "@/shared/lib/performance-document-payment-terms";
import { SimpleRichTextEditor } from "@/shared/ui/SimpleRichTextEditor";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type {
  CreatePaymentTermsTemplateDTO,
  PaymentTermsDocumentTypeDTO,
  PaymentTermsTaxVariantDTO,
  UpdatePaymentTermsTemplateDTO,
} from "../../domain/contracts";

const PaymentTermsTemplateFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ແມ່ແບບ"),
  documentType: z.enum(["quotation", "invoice", "all"]),
  taxVariant: z.enum(["without_tax", "with_tax"]),
  content: z.string().min(1, "ຕ້ອງປ້ອນເນື້ອຫາ"),
  contentEn: z.string(),
  status: z.enum(["active", "inactive"]),
  sortOrder: z.number().int(),
  isDefault: z.boolean(),
});

type PaymentTermsTemplateFormValues = z.infer<
  typeof PaymentTermsTemplateFormSchema
>;

const DOCUMENT_TYPE_OPTIONS: {
  value: PaymentTermsDocumentTypeDTO;
  label: string;
}[] = [
  { value: "quotation", label: "ໃບສະເໜີລາຄາ" },
  { value: "invoice", label: "ໃບເກັບເງິນ" },
  { value: "all", label: "ທຸກປະເພດ" },
];

const TAX_VARIANT_OPTIONS: {
  value: PaymentTermsTaxVariantDTO;
  label: string;
}[] = [
  { value: "without_tax", label: "ເອກະສານບໍ່ມີພາສີ" },
  { value: "with_tax", label: "ເອກະສານມີພາສີ" },
];

type PaymentTermsTemplateFormProps = {
  initialValues?: Partial<PaymentTermsTemplateFormValues>;
  onSubmit: (
    values: CreatePaymentTermsTemplateDTO | UpdatePaymentTermsTemplateDTO,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

export function PaymentTermsTemplateForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: PaymentTermsTemplateFormProps) {
  const methods = RHF.useForm<PaymentTermsTemplateFormValues>({
    resolver: zodResolver(PaymentTermsTemplateFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      documentType: initialValues?.documentType ?? "quotation",
      taxVariant: initialValues?.taxVariant ?? "without_tax",
      content: initialValues?.content ?? "",
      contentEn: initialValues?.contentEn ?? "",
      status: initialValues?.status ?? "active",
      sortOrder: initialValues?.sortOrder ?? 0,
      isDefault: initialValues?.isDefault ?? false,
    },
  });

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          name: vals.name,
          documentType: vals.documentType,
          taxVariant: vals.taxVariant,
          content: paymentTermsHtmlToNullable(vals.content) ?? vals.content,
          contentEn: paymentTermsHtmlToNullable(vals.contentEn),
          status: vals.status,
          sortOrder: vals.sortOrder,
          isDefault: vals.isDefault,
        });
      }}
      className="space-y-4 py-6"
    >
      <FormInput name="name" label="ຊື່ແມ່ແບບ" required />
      <div className="grid gap-4 md:grid-cols-2">
        <FormSelect
          name="documentType"
          label="ໃຊ້ກັບເອກະສານ"
          options={DOCUMENT_TYPE_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          }))}
        />
        <FormSelect
          name="taxVariant"
          label="ໃຊ້ກັບສະຖານະພາສີ"
          options={TAX_VARIANT_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          }))}
        />
      </div>
      <div className="space-y-1.5">
        <label className="font-medium text-sm">
          ເງື່ອນໄຂການຊຳລະ (ລາວ) <span className="text-destructive">*</span>
        </label>
        <SimpleRichTextEditor
          resetKey={`payment-terms-template-lo:${initialValues?.name ?? "new"}`}
          initialHtml={toPaymentTermsEditorHtml(methods.watch("content"))}
          onHtmlChange={(html) =>
            methods.setValue(
              "content",
              paymentTermsHtmlToNullable(html) ?? "",
              { shouldDirty: true, shouldValidate: true },
            )
          }
          placeholder="ຂຽນຄຳອະທິບາຍບລັອກນີ້…"
          minHeightClassName="min-h-[180px]"
        />
        {methods.formState.errors.content ? (
          <p className="text-destructive text-xs">
            {methods.formState.errors.content.message}
          </p>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <label className="font-medium text-sm">Payment terms (English)</label>
        <SimpleRichTextEditor
          resetKey={`payment-terms-template-en:${initialValues?.name ?? "new"}`}
          initialHtml={toPaymentTermsEditorHtml(methods.watch("contentEn"))}
          onHtmlChange={(html) =>
            methods.setValue(
              "contentEn",
              paymentTermsHtmlToNullable(html) ?? "",
              { shouldDirty: true },
            )
          }
          placeholder="Write payment terms in English…"
          minHeightClassName="min-h-[160px]"
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Rich text editor — ຕັ້ງແມ່ແບບເລີ່ມຕົ້ນແຍກສຳລັບມີພາສີ /
        ບໍ່ມີພາສີ · ພິມ EN ຈະໃຊ້ຂໍ້ຄວາມພາສາອັງກິດ (ຖ້າບໍ່ມີຈະ fallback ລາວ)
      </p>
      <div className="grid grid-cols-2 gap-4">
        <FormSelect
          name="status"
          label="ສະຖານະ"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
        <FormNumber name="sortOrder" label="ລຳດັບການສະແດງ" min={0} />
      </div>
      <FormCheckboxCard
        name="isDefault"
        label="ຕັ້ງເປັນແມ່ແບບເລີ່ມຕົ້ນ"
        description="ໃຊ້ເປັນຄ່າເລີ່ມຕົ້ນຕໍ່ຄູ່ ປະເພດເອກະສານ + ສະຖານະພາສີ"
      />
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
